// Sprint chatgpt-message-rail (D4) — tracks which founder message is
// "current" (the one the founder has scrolled to) for the right-edge rail.
//
// Passive `useEffect`, never `useLayoutEffect`: a sibling's layout effect
// (`useCollapseAnchor`) runs before this one and needs the scroller ref
// already attached — `MessageRail` mounts AFTER the scroller in
// `ConversationList`'s JSX for the same reason.
//
// One measurement pass per animation frame (coalesced via
// `requestAnimationFrame`), triggered by a passive `scroll` listener on
// the SAME container `useChatScroll` owns, and re-armed whenever
// `founderIds` changes (append, prepend, agent switch). State is the
// current message's id (a string) — never an index or an object: a
// primitive `setState` bails out on an unchanged value, keeping this from
// looping; an object identity would not.

import { useEffect, useState } from 'react';
import type { RefObject } from 'react';
import { BOTTOM_TOLERANCE_PX } from './useChatScroll';
import { currentIndexOf } from '../lib/message-rail';

export interface UseMessageRailArgs {
  scrollContainerRef: RefObject<HTMLDivElement>;
  founderIds: string[];
}

export function useMessageRail({
  scrollContainerRef,
  founderIds,
}: UseMessageRailArgs): string | null {
  const [currentId, setCurrentId] = useState<string | null>(null);
  // D4 — the effect depends on the joined string, not the array
  // reference, so an equal-but-new-identity `founderIds` (a fresh
  // `useMemo` result the caller re-derives every render for other
  // reasons) does not re-arm the listener.
  const idsKey = founderIds.join(',');

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || founderIds.length === 0) {
      setCurrentId(null);
      return;
    }

    const idsSet = new Set(founderIds);
    let rafId: number | null = null;

    const measure = () => {
      rafId = null;
      const container = scrollContainerRef.current;
      if (!container) return;

      const atBottom =
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - BOTTOM_TOLERANCE_PX;

      // One `querySelectorAll` per frame, filtered against the founder-id
      // set — O(DOM) per frame instead of O(n * DOM). A founder id with no
      // rendered node (should not happen — see D4's note on subagent
      // `type='user'` rows) is simply absent from `nodes` and ignored.
      const nodes = Array.from(
        container.querySelectorAll<HTMLElement>('[data-message-id]'),
      ).filter((node) => {
        const id = node.getAttribute('data-message-id');
        return id !== null && idsSet.has(id);
      });
      if (nodes.length === 0) return;

      const containerTop = container.getBoundingClientRect().top;
      const positions = nodes.map((node) => node.getBoundingClientRect().top - containerTop);
      const idx = currentIndexOf(positions, container.clientHeight / 2, atBottom);
      if (idx < 0) return;
      const node = nodes[idx];
      const id = node?.getAttribute('data-message-id');
      if (id) setCurrentId(id);
    };

    const schedule = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(measure);
    };

    schedule();
    el.addEventListener('scroll', schedule, { passive: true });
    return () => {
      el.removeEventListener('scroll', schedule);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollContainerRef, idsKey]);

  return currentId;
}
